import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { SafeImage } from "@/components/common/SafeImage";
import { productImageAlt } from "@/lib/seo/schema";


export function ProductGallery({
  images,
  name,
  badge,
  fallback,
}: {
  images: string[];
  name: string;
  badge?: string;
  fallback: string;
}) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const shots = images.length > 0 ? images : [fallback];

  const next = useCallback(() => setActive((i) => (i + 1) % shots.length), [shots.length]);
  const prev = useCallback(
    () => setActive((i) => (i - 1 + shots.length) % shots.length),
    [shots.length],
  );

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, next, prev]);

  return (
    <div className="flex flex-col gap-4 lg:flex-row-reverse lg:gap-6">
      <div className="relative flex-1">
        <div className="relative aspect-[4/5] overflow-hidden bg-[#faf7f2]">
          {badge && (
            <span className="absolute left-4 top-4 z-10 bg-white px-3 py-1 text-[10px] font-medium uppercase tracking-[0.24em] text-[#C6A15B]">
              {badge}
            </span>
          )}
          <button
            type="button"
            onClick={() => setLightbox(true)}
            aria-label={`Open ${name} image in lightbox`}
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center bg-white/90 text-black transition hover:bg-black hover:text-white"
          >
            <ZoomIn className="h-4 w-4" aria-hidden />
          </button>
          <SafeImage
            src={shots[active]}
            fallback={fallback}
            alt={productImageAlt(name, active)}
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Product images"
        className="flex gap-3 overflow-x-auto lg:w-24 lg:flex-col lg:overflow-visible"
      >
        {shots.map((src, i) => {
          const selected = i === active;
          return (
            <button
              key={src + i}
              role="tab"
              aria-selected={selected}
              aria-label={`Show image ${i + 1}`}
              onClick={() => setActive(i)}
              className={`relative aspect-square w-20 flex-shrink-0 overflow-hidden bg-[#faf7f2] transition lg:w-full ${
                selected
                  ? "outline outline-1 outline-offset-2 outline-[#C6A15B]"
                  : "opacity-70 hover:opacity-100"
              }`}
            >
              <SafeImage src={src} fallback={fallback} alt="" className="h-full w-full object-cover" />
            </button>
          );
        })}
      </div>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${name} enlarged image`}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setLightbox(false)}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setLightbox(false)}
            className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center bg-white/10 text-white transition hover:bg-white/20"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          {shots.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                className="absolute left-4 flex h-11 w-11 items-center justify-center bg-white/10 text-white transition hover:bg-white/20 md:left-8"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden />
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                className="absolute right-4 flex h-11 w-11 items-center justify-center bg-white/10 text-white transition hover:bg-white/20 md:right-8"
              >
                <ChevronRight className="h-5 w-5" aria-hidden />
              </button>
            </>
          )}
          <SafeImage
            src={shots[active]}
            fallback={fallback}
            alt={`${name} enlarged view`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] object-contain"
          />
        </div>
      )}
    </div>
  );
}
